import { useEffect, useMemo, useState } from 'react';
import {
  CompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * The only fields read for a squad's members: the profile picture for the
 * avatar. Never the email, `spec.user.entity`, ids or annotations (MC-2);
 * the display name comes from the entity presentation API.
 */
export const MEMBER_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'spec.profile.picture',
];

/** Each member's picture, by ref, in one batched call. */
export async function loadMemberPictures(
  catalogApi: CatalogApi,
  refs: string[],
): Promise<Map<string, string>> {
  const pictures = new Map<string, string>();
  if (refs.length === 0) {
    return pictures;
  }
  const { items } = await catalogApi.getEntitiesByRefs({
    entityRefs: refs,
    fields: MEMBER_FIELDS,
  });
  items.forEach((item, index) => {
    const profile = item?.spec?.profile as { picture?: unknown } | undefined;
    if (typeof profile?.picture === 'string') {
      pictures.set(refs[index], profile.picture);
    }
  });
  return pictures;
}

/**
 * The members' pictures. While they load, or when the load fails, the
 * avatars show initials: the list itself never waits for them.
 */
export function useSquadMembers(
  members: CompoundEntityRef[],
): Map<string, string> {
  const catalogApi = useApi(catalogApiRef);
  const refs = useMemo(
    () => members.map(ref => stringifyEntityRef(ref)),
    [members],
  );
  const key = refs.join(',');
  const [pictures, setPictures] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let active = true;
    loadMemberPictures(catalogApi, refs).then(
      value => active && setPictures(value),
      () => active && setPictures(new Map()),
    );
    return () => {
      active = false;
    };
    // `refs` is derived from `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, key]);
  return pictures;
}
